import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MenuUploadForm from './MenuUploadForm';
import MenuUrlExtractor from './MenuUrlExtractor';
import MenuImageImport from './MenuImageImport';
import ConfirmModal from './ConfirmModal';
import MenuReviewFeedbackModal, { REVIEW_REASON_LABELS } from './MenuReviewFeedbackModal';
import { toast } from 'react-hot-toast';
import './AdminMenuManager.css';
import { useDebounce } from 'use-debounce';
import ImpactService from '../services/impactService';

const ADMIN_MENU_FILTERS_KEY = 'adminMenuManager.filters';
const FILTER_STATUS_VALUES = new Set(['all', 'DRAFT', 'PENDING', 'APPROVED', 'REJECTED']);
const FILTER_OWNER_VALUES = new Set(['all', 'admin', 'affiliate', 'personal']);
const FILTER_SOURCE_VALUES = new Set(['all', 'ai', 'crawler']);
const FILTER_VISIBILITY_VALUES = new Set(['all', 'visible', 'hidden', 'has-feedback']);
const SORT_VALUES = new Set([
  'newest',
  'oldest',
  'title-asc',
  'title-desc',
  'views-desc',
  'views-asc',
  'orders-desc',
  'orders-asc',
  'affiliate-orders-desc',
  'affiliate-orders-asc'
]);

function readStoredMenuFilters() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(ADMIN_MENU_FILTERS_KEY) || '');
    if (!parsed || typeof parsed !== 'object') return {};
    return {
      searchTerm: typeof parsed.searchTerm === 'string' ? parsed.searchTerm : undefined,
      sortBy: SORT_VALUES.has(parsed.sortBy) ? parsed.sortBy : undefined,
      filterStatus: FILTER_STATUS_VALUES.has(parsed.filterStatus) ? parsed.filterStatus : undefined,
      filterOwner: FILTER_OWNER_VALUES.has(parsed.filterOwner) ? parsed.filterOwner : undefined,
      filterSource: FILTER_SOURCE_VALUES.has(parsed.filterSource) ? parsed.filterSource : undefined,
      filterVisibility: FILTER_VISIBILITY_VALUES.has(parsed.filterVisibility) ? parsed.filterVisibility : undefined
    };
  } catch {
    return {};
  }
}

const AdminMenuManager = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showUrlExtractor, setShowUrlExtractor] = useState(false);
  const [showImageImport, setShowImageImport] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState(() => readStoredMenuFilters().searchTerm || '');
  const [filteredMenuItems, setFilteredMenuItems] = useState([]);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState(() => readStoredMenuFilters().sortBy || 'newest');
  const [filterStatus, setFilterStatus] = useState(() => readStoredMenuFilters().filterStatus || 'all');
  const [filterOwner, setFilterOwner] = useState(() => readStoredMenuFilters().filterOwner || 'all');
  const [filterSource, setFilterSource] = useState(() => readStoredMenuFilters().filterSource || 'all');
  const [filterVisibility, setFilterVisibility] = useState(() => readStoredMenuFilters().filterVisibility || 'all');
  const itemsPerPage = 10;
  const navigate = useNavigate();
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, menuId: null, title: '' });
  const [isDeleting, setIsDeleting] = useState(false);
  const [feedbackModal, setFeedbackModal] = useState({
    isOpen: false,
    mode: 'feedback',
    menuId: null,
    title: ''
  });
  const [isSavingFeedback, setIsSavingFeedback] = useState(false);

  useEffect(() => {
    fetchMenuItems();
    fetchOrders();
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(ADMIN_MENU_FILTERS_KEY, JSON.stringify({
        searchTerm,
        sortBy,
        filterStatus,
        filterOwner,
        filterSource,
        filterVisibility
      }));
    } catch {
      // sessionStorage may be unavailable
    }
  }, [searchTerm, sortBy, filterStatus, filterOwner, filterSource, filterVisibility]);

  useEffect(() => {
    let filtered = menuItems.filter(item => {
      const matchesSearch = !debouncedSearchTerm || 
        item.title.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        (item.ingredients && item.ingredients.some(ingredient => 
          (typeof ingredient === 'object' && ingredient.name 
            ? ingredient.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
            : String(ingredient).toLowerCase().includes(debouncedSearchTerm.toLowerCase()))
        ));

      const status = item.moderation_status || 'DRAFT';
      const matchesStatus = filterStatus === 'all' || status === filterStatus;

      let matchesOwner = true;
      if (filterOwner === 'admin') {
        matchesOwner = !item.affiliateId && !item.isPersonal;
      } else if (filterOwner === 'affiliate') {
        matchesOwner = !!item.affiliateId;
      } else if (filterOwner === 'personal') {
        matchesOwner = !!item.isPersonal;
      }

      let matchesSource = true;
      if (filterSource === 'ai') {
        matchesSource = item.created_by === 'ai_menu_agent';
      } else if (filterSource === 'crawler') {
        matchesSource = item.created_by === 'allrecipes_crawler';
      }

      let matchesVisibility = true;
      if (filterVisibility === 'hidden') {
        matchesVisibility = !!item.hidden;
      } else if (filterVisibility === 'visible') {
        matchesVisibility = !item.hidden;
      } else if (filterVisibility === 'has-feedback') {
        matchesVisibility = Array.isArray(item.reviewerFeedback) && item.reviewerFeedback.length > 0;
      }

      return matchesSearch && matchesStatus && matchesOwner && matchesSource && matchesVisibility;
    });

    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'views-desc':
          return (b.metrics?.views || 0) - (a.metrics?.views || 0);
        case 'views-asc':
          return (a.metrics?.views || 0) - (b.metrics?.views || 0);
        case 'orders-desc':
          return (b.metrics?.orders || 0) - (a.metrics?.orders || 0);
        case 'orders-asc':
          return (a.metrics?.orders || 0) - (b.metrics?.orders || 0);
        case 'affiliate-orders-desc':
          return (b.metrics?.affiliateOrders || 0) - (a.metrics?.affiliateOrders || 0);
        case 'affiliate-orders-asc':
          return (a.metrics?.affiliateOrders || 0) - (b.metrics?.affiliateOrders || 0);
        case 'newest':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'title-asc':
          return a.title.localeCompare(b.title);
        case 'title-desc':
          return b.title.localeCompare(a.title);
        default:
          return 0;
      }
    });
    
    setFilteredMenuItems(sorted);
    setCurrentPage(1);
  }, [debouncedSearchTerm, menuItems, sortBy, filterStatus, filterOwner, filterSource, filterVisibility]);

  const fetchMenuItems = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/menu-items`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch menu items');
      }

      const data = await response.json();
      setMenuItems(data);
      setFilteredMenuItems(data);
    } catch (err) {
      console.error('Error fetching menu items:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const syncImpactStats = async () => {
    try {
      await ImpactService.syncStats();
      // Refresh menu items to get updated stats
      fetchMenuItems();
      toast.success('Impact.com stats synced successfully');
    } catch (error) {
      console.error('Error syncing Impact stats:', error);
      // Check if it's a configuration issue
      if (error.message?.includes('API') || error.message?.includes('credentials')) {
        toast.error('Impact.com API is not configured. Please check your credentials.');
      } else {
        toast.error('Failed to sync Impact.com stats');
      }
    }
  };

  const fetchOrders = async () => {
    try {
      setOrdersLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/impact/orders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        // Don't show error toast for empty/unavailable data, just log it
        if (response.status === 500) {
          console.warn('Impact orders API is unavailable - this may be due to missing API credentials');
          setOrders([]);
          return;
        }
        throw new Error('Failed to fetch orders');
      }

      const data = await response.json();
      setOrders(data.data || []);
    } catch (err) {
      console.error('Error fetching orders:', err);
      // Only show toast for non-500 errors (500 likely means API not configured)
      if (!err.message?.includes('500')) {
        toast.error('Failed to fetch orders');
      }
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handleUploadSuccess = (newItem) => {
    setMenuItems(prev => {
      // If we're editing, replace the old item
      if (editingItem) {
        return prev.map(item => item._id === editingItem._id ? newItem : item);
      }
      // Otherwise add the new item
      return [...prev, newItem];
    });
    setEditingItem(null);
    setShowUploadForm(false);
  };

  const handleExtractSuccess = (extractedData) => {
    // Normalize contentUrl and cuisine fields for the form
    const normalizedData = {
      ...extractedData,
      contentUrl: extractedData.contentUrl || extractedData.url || extractedData.sourceUrl || '',
      image: extractedData.image || extractedData.imageUrl || extractedData.mainImageUrl || '',
      cuisine: extractedData.cuisine || '',
    };
    setEditingItem(normalizedData);
    setShowUploadForm(true);
    setShowUrlExtractor(false);
    setShowImageImport(false);
    window.scrollTo(0, 0);
  };

  const handleImageImportSuccess = (extractedData) => {
    // Normalize the extracted data for the form
    const normalizedData = {
      ...extractedData,
      contentUrl: extractedData.contentUrl || '',
      cuisine: extractedData.cuisine || '',
    };
    setEditingItem(normalizedData);
    setShowUploadForm(true);
    setShowImageImport(false);
    setShowUrlExtractor(false);
    window.scrollTo(0, 0);
    
    // Show confidence info if available
    if (extractedData._extractionConfidence) {
      const confidence = extractedData._extractionConfidence;
      const missingFields = confidence.missingFields || [];
      if (missingFields.length > 0) {
        toast(`Some fields couldn't be extracted: ${missingFields.join(', ')}. Please review and complete the form.`, {
          duration: 5000,
          icon: '⚠️'
        });
      }
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowUploadForm(true);
    window.scrollTo(0, 0);
  };

  const handleViewDetails = (menuId) => {
    navigate(`/menu/${menuId}?context=admin`);
  };

  const handleShare = async (menuId) => {
    try {
      const menuUrl = `${window.location.origin}/menu/${menuId}`;
      await navigator.clipboard.writeText(menuUrl);
      toast.success('Menu URL copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy URL');
    }
  };

  const handleApprove = async (menuId) => {
    try {
      const token = localStorage.getItem('token');
      const menuItem = menuItems.find(item => item.menuId === menuId);
      if (!menuItem) {
        throw new Error('Menu item not found');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/menu-items/${menuItem._id}/approve`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to approve menu item');
      }

      // Update the menu item moderation_status in state
      setMenuItems(prev => prev.map(item => 
        item.menuId === menuId ? { ...item, moderation_status: 'APPROVED' } : item
      ));
      toast.success('Menu item approved successfully');
    } catch (err) {
      console.error('Error approving menu item:', err);
      toast.error('Failed to approve menu item');
    }
  };

  const handleDeleteClick = (menuId, title) => {
    setDeleteConfirm({ isOpen: true, menuId, title });
  };

  const applyMenuItemUpdate = (menuId, menuItem) => {
    setMenuItems(prev => prev.map(item =>
      item.menuId === menuId
        ? { ...item, hidden: menuItem.hidden, reviewerFeedback: menuItem.reviewerFeedback || item.reviewerFeedback }
        : item
    ));
  };

  const closeFeedbackModal = () => {
    setFeedbackModal({ isOpen: false, mode: 'feedback', menuId: null, title: '' });
  };

  const handleToggleHidden = async (menuId, feedback) => {
    try {
      const token = localStorage.getItem('token');
      const menuItem = menuItems.find(item => item.menuId === menuId);
      if (!menuItem) {
        throw new Error('Menu item not found');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/menu-items/${menuItem._id}/toggle-hidden`, {
        method: 'PATCH',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(feedback ? { feedback } : {})
      });

      if (!response.ok) {
        throw new Error('Failed to toggle menu item visibility');
      }

      const data = await response.json();
      applyMenuItemUpdate(menuId, data.menuItem);
      toast.success(
        data.menuItem.hidden
          ? (feedback ? 'Hidden and feedback saved for later agent runs' : 'Menu item hidden')
          : 'Menu item is now visible'
      );
      return true;
    } catch (err) {
      console.error('Error toggling menu item visibility:', err);
      toast.error('Failed to toggle visibility');
      return false;
    }
  };

  const handleHideClick = (item) => {
    if (item.hidden) {
      handleToggleHidden(item.menuId);
      return;
    }
    setFeedbackModal({
      isOpen: true,
      mode: 'hide',
      menuId: item.menuId,
      title: item.title
    });
  };

  const handleFeedbackClick = (item) => {
    setFeedbackModal({
      isOpen: true,
      mode: 'feedback',
      menuId: item.menuId,
      title: item.title
    });
  };

  const handleFeedbackSubmit = async ({ reason, note, hide }) => {
    const { menuId, mode } = feedbackModal;
    const menuItem = menuItems.find(item => item.menuId === menuId);
    if (!menuItem) return;

    setIsSavingFeedback(true);
    try {
      if (mode === 'hide' && hide) {
        const ok = await handleToggleHidden(menuId, { reason, note });
        if (ok) closeFeedbackModal();
        return;
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/menu-items/${menuItem._id}/reviewer-feedback`, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason, note, hide })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to save feedback');
      }

      const data = await response.json();
      applyMenuItemUpdate(menuId, data.menuItem);
      toast.success(hide ? 'Feedback saved and menu item hidden' : 'Feedback saved for later agent runs');
      closeFeedbackModal();
    } catch (err) {
      console.error('Error saving reviewer feedback:', err);
      toast.error(err.message || 'Failed to save feedback');
    } finally {
      setIsSavingFeedback(false);
    }
  };

  const handleHideWithoutFeedback = async () => {
    const { menuId } = feedbackModal;
    setIsSavingFeedback(true);
    try {
      const ok = await handleToggleHidden(menuId);
      if (ok) closeFeedbackModal();
    } finally {
      setIsSavingFeedback(false);
    }
  };

  const handleDeleteConfirm = async () => {
    const { menuId } = deleteConfirm;
    setIsDeleting(true);

    try {
      const token = localStorage.getItem('token');
      const menuItem = menuItems.find(item => item.menuId === menuId);
      if (!menuItem) {
        throw new Error('Menu item not found');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/menu-items/${menuItem._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete menu item');
      }

      setMenuItems(prev => prev.filter(item => item.menuId !== menuId));
      toast.success('Menu item deleted successfully');
      setDeleteConfirm({ isOpen: false, menuId: null, title: '' });
    } catch (err) {
      console.error('Error deleting menu item:', err);
      toast.error('Failed to delete menu item');
    } finally {
      setIsDeleting(false);
    }
  };

  // Add pagination calculation
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredMenuItems.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredMenuItems.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  // Pagination component
  const Pagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="pagination">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="page-button"
        >
          Previous
        </button>
        <span className="page-info">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="page-button"
        >
          Next
        </button>
      </div>
    );
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return (
      <div className="error">
        <p>{error}</p>
        <button className="retry-button" onClick={fetchMenuItems}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="admin-menu-manager">
      {/* Order Management Section */}
      <div className="order-management-section">
        <h2 className="section-title">Order Management</h2>
        <div className="orders-section">
          <div className="section-header">
            <div className="header-left">
              <button 
                className="sync-stats-button"
                onClick={() => {
                  syncImpactStats();
                  fetchOrders();
                }}
              >
                <i className="fas fa-sync"></i> Sync Impact Data
              </button>
            </div>
          </div>

          <div className="orders-table-container">
            {ordersLoading ? (
              <div className="loading">Loading orders...</div>
            ) : (
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Sale Amount</th>
                    <th>Payout</th>
                    <th>Menu Item</th>
                    <th>Affiliate</th>
                    <th>Landing Page URL</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order, index) => (
                    <tr key={order.action_id || index}>
                      <td>{formatDate(order.action_date)}</td>
                      <td>
                        <span className={`status-badge ${order.status.toLowerCase()}`}>
                          {order.status}
                        </span>
                      </td>
                      <td>{formatCurrency(order.sale_amount)}</td>
                      <td>{formatCurrency(order.payout)}</td>
                      <td>
                        {order.menuItem ? (
                          <a href="#" onClick={() => handleViewDetails(order.menuItem._id)}>
                            {order.menuItem.title}
                          </a>
                        ) : (
                          <span className="text-muted">No menu item</span>
                        )}
                      </td>
                      <td>
                        {order.affiliate ? (
                          <div>
                            <div>{order.affiliate.businessName}</div>
                            <small className="text-muted">
                              {(order.affiliate.commissionRate * 100).toFixed(1)}% commission
                            </small>
                          </div>
                        ) : (
                          <span className="text-muted">No affiliate</span>
                        )}
                      </td>
                      <td>
                        <a href={order.ref_landingpage_url} target="_blank" rel="noopener noreferrer">
                          View Page
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Menu Management Section */}
      <div className="menu-management-section">
        <h2 className="section-title">Menu Management</h2>
        <div className="section-header">
          <div className="menu-actions">
            <button 
              className="add-menu-button"
              onClick={() => {
                setEditingItem(null);
                setShowUploadForm(!showUploadForm);
                setShowUrlExtractor(false);
                setShowImageImport(false);
              }}
            >
              <i className="fas fa-plus"></i> {showUploadForm ? 'Cancel' : 'Add Menu Item'}
            </button>
            <button 
              className="extract-menu-button"
              onClick={() => {
                setShowUrlExtractor(!showUrlExtractor);
                setShowUploadForm(false);
                setShowImageImport(false);
                setEditingItem(null);
              }}
            >
              <i className="fas fa-link"></i> {showUrlExtractor ? 'Cancel' : 'Extract from URL'}
            </button>
            <button 
              className="image-import-button"
              onClick={() => {
                setShowImageImport(!showImageImport);
                setShowUrlExtractor(false);
                setShowUploadForm(false);
                setEditingItem(null);
              }}
            >
              <i className="fas fa-camera"></i> {showImageImport ? 'Cancel' : 'Import from Image'}
            </button>
          </div>
        </div>

        {showUrlExtractor && (
              <MenuUrlExtractor onExtractSuccess={handleExtractSuccess} />
            )}

            {showImageImport && (
              <MenuImageImport 
                onExtractSuccess={handleImageImportSuccess}
                onCancel={() => setShowImageImport(false)}
                apiEndpoint="/admin/extract-menu-from-images"
                imageGenEndpoint="/admin/generate-menu-image"
              />
            )}

            {showUploadForm && (
              <MenuUploadForm
                onSuccess={handleUploadSuccess}
                initialData={editingItem}
                isAdmin={true}
              />
            )}

            <div className="search-and-sort-container">
              <div className="admin-menu-search-container">
                <input
                  type="text"
                  placeholder="Search by name, description, or ingredients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="admin-menu-search-input"
                />
              </div>
              <div className="filter-sort-row">
                <div className="filter-container">
                  <label htmlFor="filter-status">Status:</label>
                  <select
                    id="filter-status"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All Statuses</option>
                    <option value="DRAFT">Draft</option>
                    <option value="PENDING">Pending</option>
                    <option value="APPROVED">Approved</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                </div>
                <div className="filter-container">
                  <label htmlFor="filter-owner">Owner:</label>
                  <select
                    id="filter-owner"
                    value={filterOwner}
                    onChange={(e) => setFilterOwner(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All Owners</option>
                    <option value="admin">Admin / FreshFitLab</option>
                    <option value="affiliate">Affiliate</option>
                    <option value="personal">Personal User</option>
                  </select>
                </div>
                <div className="filter-container">
                  <label htmlFor="filter-source">Source:</label>
                  <select
                    id="filter-source"
                    value={filterSource}
                    onChange={(e) => setFilterSource(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All Sources</option>
                    <option value="ai">AI Menu Agent</option>
                    <option value="crawler">AllRecipes Crawler</option>
                  </select>
                </div>
                <div className="filter-container">
                  <label htmlFor="filter-visibility">Review:</label>
                  <select
                    id="filter-visibility"
                    value={filterVisibility}
                    onChange={(e) => setFilterVisibility(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All items</option>
                    <option value="visible">Visible</option>
                    <option value="hidden">Hidden</option>
                    <option value="has-feedback">Has reviewer feedback</option>
                  </select>
                </div>
                <div className="sort-container">
                  <label htmlFor="sort-select">Sort by:</label>
                  <select
                    id="sort-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="sort-select"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="title-asc">Title (A-Z)</option>
                    <option value="title-desc">Title (Z-A)</option>
                    <option value="views-desc">Most Views</option>
                    <option value="views-asc">Least Views</option>
                    <option value="orders-desc">Most Orders</option>
                    <option value="orders-asc">Least Orders</option>
                    <option value="affiliate-orders-desc">Most Affiliate Orders</option>
                    <option value="affiliate-orders-asc">Least Affiliate Orders</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="menu-items-grid">
              {currentItems.map(item => (
                <div key={item._id} className={`menu-item-card${item.hidden ? ' menu-item-hidden' : ''}`}>
                  <img 
                    src={item.image} 
                    alt={item.title} 
                    className="item-image"
                    onClick={() => handleViewDetails(item.menuId)}
                  />
                  <div className="item-details">
                    <h3>{item.title} {item.hidden && <span className="hidden-badge">Hidden</span>}</h3>
                    <p>{item.description}</p>
                    <p className="cuisine">Cuisine: {item.cuisine}</p>
                    <p className="status">
                      Status: <span className={`status-badge status-${(item.moderation_status || 'DRAFT').toLowerCase()}`}>
                        {item.moderation_status || 'DRAFT'}
                      </span>
                    </p>
                    {item.qcIssues?.length > 0 && (
                      <details className="qc-issues">
                        <summary>
                          <i className="fas fa-triangle-exclamation"></i>
                          {' '}
                          {item.qcIssues.length} import issue{item.qcIssues.length === 1 ? '' : 's'}
                        </summary>
                        <ul>
                          {item.qcIssues.map((issue, issueIndex) => (
                            <li key={issueIndex}>{issue}</li>
                          ))}
                        </ul>
                        {item.buildHistory?.length > 0 && (
                          <ol className="qc-attempts">
                            {item.buildHistory.map((attempt) => (
                              <li key={attempt.attempt}>
                                <strong>Attempt {attempt.attempt}:</strong>
                                {attempt.applied?.length ? ` repaired ${attempt.applied.join(', ')}` : ' nothing repaired'}
                                {attempt.failed?.length ? `; could not repair ${attempt.failed.join(', ')}` : ''}
                              </li>
                            ))}
                          </ol>
                        )}
                      </details>
                    )}
                    {item.reviewerFeedback?.length > 0 && (
                      <details className="reviewer-feedback">
                        <summary>
                          <i className="fas fa-comment-dots"></i>
                          {' '}
                          {item.reviewerFeedback.length} reviewer note{item.reviewerFeedback.length === 1 ? '' : 's'}
                        </summary>
                        <ul>
                          {item.reviewerFeedback.map((entry, feedbackIndex) => (
                            <li key={entry._id || feedbackIndex}>
                              <strong>{REVIEW_REASON_LABELS[entry.reason] || entry.reason}</strong>
                              {entry.note ? ` — ${entry.note}` : ''}
                              {entry.createdAt && (
                                <span className="reviewer-feedback-date">
                                  {' '}
                                  ({new Date(entry.createdAt).toLocaleDateString()})
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                    <p className="owner">
                      Owner: {item.affiliateId 
                        ? `Affiliate (${item.affiliateBusinessName || item.affiliateEmail || 'Unknown'})` 
                        : (item.isPersonal 
                          ? `Personal User (${item.userEmail || 'Unknown'})` 
                          : 'Admin')}
                    </p>
                    <div className="metrics-row">
                      <div className="metric">
                        <span className="metric-label">Views:</span>
                        <span className="metric-value">{item.metrics?.views || 0}</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">Orders:</span>
                        <span className="metric-value">{item.metrics?.orders || 0}</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">Affiliate Orders:</span>
                        <span className="metric-value">{item.metrics?.affiliateOrders || 0}</span>
                      </div>
                    </div>
                    <div className="item-actions">
                      <button
                        className="view-details-button"
                        onClick={() => handleViewDetails(item.menuId)}
                      >
                        View Details
                      </button>
                      <button
                        className="edit-button"
                        onClick={() => handleEdit(item)}
                      >
                        Edit
                      </button>
                      {(item.moderation_status === 'DRAFT') && (
                        <button 
                          className="approve-button publish-button"
                          onClick={() => handleApprove(item.menuId)}
                          title="Publish menu item (set to APPROVED)"
                        >
                          <i className="fas fa-globe"></i> Make Public
                        </button>
                      )}
                      {(item.moderation_status === 'PENDING' || item.status === 'PENDING') && (
                        <button 
                          className="approve-button"
                          onClick={() => handleApprove(item.menuId)}
                          title="Approve menu item"
                        >
                          <i className="fas fa-check"></i> Approve
                        </button>
                      )}
                      <button 
                        className="share-button"
                        onClick={() => handleShare(item.menuId)}
                        title="Copy menu URL to clipboard"
                      >
                        <i className="fas fa-share-alt"></i> Share
                      </button>
                      <button
                        className="feedback-button"
                        onClick={() => handleFeedbackClick(item)}
                        title="Add feedback the agents can learn from"
                      >
                        <i className="fas fa-comment-dots"></i> Feedback
                      </button>
                      <button 
                        className={`hide-button ${item.hidden ? 'is-hidden' : ''}`}
                        onClick={() => handleHideClick(item)}
                        title={item.hidden ? 'Show menu item' : 'Hide menu item and optionally leave feedback'}
                      >
                        <i className={`fas ${item.hidden ? 'fa-eye' : 'fa-eye-slash'}`}></i> {item.hidden ? 'Show' : 'Hide'}
                      </button>
                      <button 
                        className="delete-button"
                        onClick={() => handleDeleteClick(item.menuId, item.title)}
                        title="Delete menu item"
                      >
                        <i className="fas fa-trash"></i> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <Pagination />
      </div>

      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        onCancel={() => setDeleteConfirm({ isOpen: false, menuId: null, title: '' })}
        onConfirm={handleDeleteConfirm}
        title="Delete Menu Item"
        message={`Are you sure you want to delete "${deleteConfirm.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        loading={isDeleting}
      />

      <MenuReviewFeedbackModal
        isOpen={feedbackModal.isOpen}
        mode={feedbackModal.mode}
        itemTitle={feedbackModal.title}
        loading={isSavingFeedback}
        onCancel={closeFeedbackModal}
        onSubmit={handleFeedbackSubmit}
        onHideWithoutFeedback={feedbackModal.mode === 'hide' ? handleHideWithoutFeedback : undefined}
      />
    </div>
  );
};

export default AdminMenuManager; 